import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { get_orders, get_guest_orders, cancel_order } from '../../store/reducers/orderReducer'
import toast from 'react-hot-toast'

const Orders = ({ sellerId, sellerName }) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [searchParams] = useSearchParams()
    const { userInfo } = useSelector(state => state.auth)
    const { myOrders, order, guestCustomer, errorMessage, successMessage, loading } = useSelector(state => state.order)
    const [state, setState] = useState('all')
    const [guestEmail, setGuestEmail] = useState('')
    const [guestPhone, setGuestPhone] = useState('')
    const [isGuestMode, setIsGuestMode] = useState(false)
    const [cancelOrderNumber, setCancelOrderNumber] = useState(null)
    const [showCancelConfirm, setShowCancelConfirm] = useState(false)

    // Check if we're in guest mode (no userInfo but have email/phone)
    useEffect(() => {
        const email = searchParams.get('email') || localStorage.getItem('guestEmail')
        const phone = searchParams.get('phone') || localStorage.getItem('guestPhone')
        
        if (!userInfo && (email || phone)) {
            setIsGuestMode(true)
            setGuestEmail(email || '')
            setGuestPhone(phone || '')
        } else if (userInfo) {
            setIsGuestMode(false)
        } else {
            // No userInfo and no guest data - show tracking form
            setIsGuestMode(true)
            setGuestEmail('')
            setGuestPhone('')
        }
    }, [userInfo, searchParams])

    useEffect(() => {
        if (isGuestMode && (guestEmail || guestPhone)) {
            console.log('Fetching guest orders with:', { email: guestEmail, phone: guestPhone });
            dispatch(get_guest_orders({ email: guestEmail, phone: guestPhone }))
        } else if (userInfo) {
            console.log('Fetching user orders with:', { status: state, customerId: userInfo.id });
            dispatch(get_orders({ status: state, customerId: userInfo.id }))
        }
    }, [state, userInfo, isGuestMode, guestEmail, guestPhone])

    // Show error messages
    useEffect(() => {
        if (errorMessage) {
            toast.error(errorMessage)
        }
    }, [errorMessage])

    // Show success messages
    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage)
        }
    }, [successMessage])

    // Debug logging
    useEffect(() => {
        console.log('Orders component state:', {
            isGuestMode,
            userInfo: !!userInfo,
            myOrders: myOrders.length,
            guestCustomer: !!guestCustomer,
            guestEmail,
            guestPhone
        });
    }, [isGuestMode, userInfo, myOrders, guestCustomer, guestEmail, guestPhone]);

    const calculateDiscountedPrice = (product) => {
        if (!product) return 0;
      
        const { price = 0, discount = 0, bulkPricing = [] } = product;
        const quantity = product.quantity || 1;
        let addonsPrice = 0;
      
        // Calculate addons price
        if (product.addons && product.addons.length > 0) {
            addonsPrice = product.addons.reduce((total, addon) => {
                return total + (addon.price || 0);
            }, 0);
        }
      
        // First check for bulk pricing
        if (bulkPricing && bulkPricing.length > 0) {
            // Filter out invalid bulk prices and sort by quantity
            const validBulkPricing = bulkPricing
                .filter(bp => bp.quantity != null && bp.price != null)
                .sort((a, b) => b.quantity - a.quantity);
      
            // Find applicable bulk price
            const applicableBulkPrice = validBulkPricing.find(
                bp => quantity >= bp.quantity
            );
      
            if (applicableBulkPrice && applicableBulkPrice.price) {
                return (applicableBulkPrice.price * quantity) + (addonsPrice * quantity);
            }
        }
      
        // If no bulk pricing applies, calculate regular price with discount
        const basePrice = discount > 0 ? price - (price * discount / 100) : price;
        return (basePrice * quantity) + (addonsPrice * quantity);
    };

    const redirect = (ord) => {
        let items = 0;
        for (let i = 0; i < ord.products.length; i++) {
            items = ord.products[i].quantity + items;
        }
        navigate(`/${sellerName}/payment`, {
            state: {
                price: ord.price,
                items,
                orderId: ord.orderNumber,
                currencySymbol: ord.products[0]?.currency || "NA"
            }
        })
    }

    // Guest order tracking form
    const handleGuestTracking = (e) => {
        e.preventDefault()
        if (guestEmail || guestPhone) {
            localStorage.setItem('guestEmail', guestEmail)
            localStorage.setItem('guestPhone', guestPhone)
            dispatch(get_guest_orders({ email: guestEmail, phone: guestPhone }))
        } else {
            toast.error('Please enter either email or phone number')
        }
    }

    // Handle cancel order
    const handleCancelOrder = (orderNumber) => {
        setCancelOrderNumber(orderNumber)
        setShowCancelConfirm(true)
    }

    const confirmCancelOrder = async () => {
        if (cancelOrderNumber) {
            try {
                await dispatch(cancel_order({ orderNumber: cancelOrderNumber })).unwrap()
                setShowCancelConfirm(false)
                setCancelOrderNumber(null)
                
                // Refresh orders after cancellation
                if (isGuestMode && (guestEmail || guestPhone)) {
                    dispatch(get_guest_orders({ email: guestEmail, phone: guestPhone }))
                } else if (userInfo) {
                    dispatch(get_orders({ status: state, customerId: userInfo.id }))
                }
            } catch (error) {
                console.error('Failed to cancel order:', error)
            }
        }
    }

    const cancelCancelOrder = () => {
        setShowCancelConfirm(false)
        setCancelOrderNumber(null)
    }

    // Check if order can be cancelled
    const canCancelOrder = (order) => {
        // Can't cancel if already cancelled or delivered
        if (order.delivery_status === 'cancelled' || order.delivery_status === 'delivered') {
            return false
        }
        
        // Can't cancel if payment is already refunded
        if (order.payment_status === 'refunded') {
            return false
        }
        
        // Can cancel if order is placed, pending, or in warehouse
        return ['placed', 'pending', 'warehouse'].includes(order.delivery_status)
    }

    // Get order status color
    const getOrderStatusColor = (status) => {
        switch (status) {
            case 'delivered':
                return 'bg-green-100 text-green-800'
            case 'placed':
                return 'bg-blue-100 text-blue-800'
            case 'pending':
                return 'bg-yellow-100 text-yellow-800'
            case 'warehouse':
                return 'bg-purple-100 text-purple-800'
            case 'cancelled':
                return 'bg-red-100 text-red-800'
            default:
                return 'bg-gray-100 text-gray-800'
        }
    }

    // If in guest mode and no orders found, show tracking form
    if (isGuestMode && myOrders.length === 0) {
        return (
            <div className="bg-white p-6 rounded-md">
                <h2 className="text-xl font-semibold text-slate-600 mb-4">Track Your Order</h2>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <p className="text-blue-800 text-sm">
                        Enter your email or phone number to track your order. If you placed an order as a guest, 
                        you can track it using the contact information you provided during checkout.
                    </p>
                </div>
                
                <form onSubmit={handleGuestTracking} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address
                        </label>
                        <input
                            type="email"
                            value={guestEmail}
                            onChange={(e) => setGuestEmail(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Enter your email address"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Phone Number
                        </label>
                        <input
                            type="tel"
                            value={guestPhone}
                            onChange={(e) => setGuestPhone(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Enter your phone number"
                        />
                    </div>
                    
                    <button
                        type="submit"
                        className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors"
                    >
                        Track Order
                    </button>
                </form>
                
                <div className="mt-6 text-center">
                    <p className="text-sm text-gray-600">
                        Having trouble? Contact support with your order number.
                    </p>
                </div>
            </div>
        )
    }

    return (
      <div className="bg-white p-4 rounded-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-slate-600">
            {isGuestMode ? 'Guest Orders' : 'My Orders'}
          </h2>
          {!isGuestMode && (
            <select
              className="outline-none px-3 py-1 border rounded-md text-slate-600"
              value={state}
              onChange={(e) => setState(e.target.value)}
            >
              <option value="all">--order status---</option>
              <option value="placed">Placed</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
              <option value="warehouse">Warehouse</option>
            </select>
          )}
        </div>

        {/* Guest customer info */}
        {isGuestMode && guestCustomer && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <p className="text-green-800 text-sm">
              Welcome back, <strong>{guestCustomer.name}</strong>! 
              We found your orders using {guestCustomer.email ? 'email' : 'phone number'}.
            </p>
          </div>
        )}

        {/* No orders message */}
        {myOrders.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500 text-lg">
              {isGuestMode 
                ? 'No orders found for the provided contact information.' 
                : 'No orders found.'
              }
            </p>
            {isGuestMode && (
              <button
                onClick={() => {
                  setGuestEmail('')
                  setGuestPhone('')
                  setIsGuestMode(true)
                }}
                className="mt-4 text-indigo-600 hover:text-indigo-700"
              >
                Try different contact information
              </button>
            )}
          </div>
        )}

        {/* Orders table */}
        {myOrders.length > 0 && (
          <div className="pt-4">
            <div className="relative overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-500">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                    
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {myOrders.map((o, i) => {
                      const currency = o.products[0]?.currency || "NA";
                      const items = o.products.reduce((total, product) => total + (product.quantity || 1), 0);
                      const shipping_fee = Array.isArray(o.shipping_fee) ? o.shipping_fee[0] : o.shipping_fee;
                      
                      return (
                          <tr key={i} className="bg-white border-b hover:bg-gray-50">
                              <td className="px-6 py-4 font-medium whitespace-nowrap">
                                  #{o.orderNumber}
                              </td>
                              <td className="px-6 py-4 font-medium whitespace-nowrap">
                                  {o.date}
                              </td>
                              <td className="px-6 py-4 font-medium whitespace-nowrap">
                                  {items} items
                              </td>
                             
                              <td className="px-6 py-4 font-medium whitespace-nowrap">
                                  {(() => {
                                      // Use authororders.price if available, otherwise fallback to calculated price
                                      if (o.authorOrdersData && o.authorOrdersData.length > 0) {
                                          // Sum up all authororders prices
                                          const totalAuthorOrdersPrice = o.authorOrdersData.reduce((total, authorOrder) => {
                                              return total + (authorOrder.price || 0);
                                          }, 0);
                                          return `${currency} ${totalAuthorOrdersPrice.toFixed(2)}`;
                                      } else {
                                          // Fallback to calculated price if no authororders data
                                          const subtotal = Array.isArray(o.products) 
                                              ? o.products.reduce((total, product) => {
                                                  return total + calculateDiscountedPrice(product);
                                              }, 0)
                                              : 0
                                          const shippingFee = Array.isArray(o.shipping_fee) ? o.shipping_fee[0] : o.shipping_fee;
                                          const actualTotal = subtotal + Number(shippingFee || 0);
                                          return `${currency} ${actualTotal.toFixed(2)}`;
                                      }
                                  })()}
                              </td>
                              <td className="px-6 py-4 font-medium whitespace-nowrap">
                                  <span
                                      className={`py-[1px] text-xs px-3 ${
                                          o.payment_status === "paid"
                                              ? "bg-green-100 text-green-800"
                                              : "bg-red-100 text-red-800"
                                      } rounded-md`}
                                  >
                                      {o.payment_status}
                                  </span>
                              </td>
                              <td className="px-6 py-4 font-medium whitespace-nowrap">
                                  <span
                                      className={getOrderStatusColor(o.delivery_status)}
                                  >
                                      {o.delivery_status}
                                  </span>
                              </td>
                              <td className="px-6 py-4 font-medium whitespace-nowrap">
                                  <div className="flex space-x-6 items-center">
                                      <Link
                                          to={`/${sellerName}/dashboard/order/details/${o.orderNumber}`}
                                          className="text-indigo-600 hover:text-indigo-900"
                                      >
                                          View Details
                                      </Link>
                                      {o.payment_status !== 'paid' && o.delivery_status !== 'cancelled' && (
                                          <button
                                              onClick={() => {
                                                  console.log('Pay Now clicked for order:', o);
                                                  console.log('Order products:', o.products);
                                                  
                                                  // Ensure gateways that rely on localStorage have the order number
                                                  try { localStorage.setItem('orderNumber', o.orderNumber); } catch (_) {}

                                                  // Derive summary fields
                                                  const currencySymbol = o.products?.[0]?.currency || 'NA'
                                                  const itemsCount = Array.isArray(o.products)
                                                      ? o.products.reduce((total, p) => total + (p.quantity || 1), 0)
                                                      : 0
                                                  const shippingFee = Array.isArray(o.shipping_fee) ? (o.shipping_fee[0] || 0) : (o.shipping_fee || 0)
                                                  
                                                  // Use authororders.price if available, otherwise calculate from products
                                                  let subtotal, total;
                                                  if (o.authorOrdersData && o.authorOrdersData.length > 0) {
                                                      // Sum up all authororders prices (these already include shipping)
                                                      subtotal = o.authorOrdersData.reduce((total, authorOrder) => {
                                                          return total + (authorOrder.price || 0);
                                                      }, 0);
                                                      total = subtotal; // authororders.price already includes shipping
                                                  } else {
                                                      // Fallback to calculated price if no authororders data
                                                      subtotal = Array.isArray(o.products) 
                                                          ? o.products.reduce((total, product) => {
                                                              return total + calculateDiscountedPrice(product);
                                                          }, 0)
                                                          : 0
                                                      total = subtotal + Number(shippingFee || 0);
                                                  }

                                                  // Prefer shippingInfo from order if available, else fallback to saved
                                                  let shippingInfo = o.shippingInfo || {}
                                                  if (!shippingInfo || Object.keys(shippingInfo).length === 0) {
                                                      try {
                                                          const saved = localStorage.getItem('shippingInfo')
                                                          if (saved) shippingInfo = JSON.parse(saved)
                                                      } catch (_) {}
                                                  }

                                                  // Ensure products have the required structure for shipping page
                                                  const formattedProducts = (o.products || []).map(product => ({
                                                      ...product,
                                                      // Ensure required fields exist
                                                      currency: product.currency || currencySymbol,
                                                      name: product.name || 'Product',
                                                      images: product.images || [],
                                                      price: product.price || 0,
                                                      quantity: product.quantity || 1,
                                                      // Add any missing fields that shipping page expects
                                                      _id: product._id || product.productId,
                                                      sellerId: product.sellerId || sellerId
                                                  }));

                                                  const orderSummary = {
                                                      subtotal,
                                                      shippingFee: Number(shippingFee || 0),
                                                      total,
                                                      currencySymbol,
                                                      items: itemsCount,
                                                      orderNumber: o.orderNumber,
                                                      sellerId,
                                                      products: formattedProducts,
                                                      shippingInfo
                                                  }

                                                  console.log('Navigating to payment with orderSummary:', orderSummary);
                                                  navigate(`/${sellerName}/payment`, {
                                                      state: { orderSummary }
                                                  })
                                              }}
                                              className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                                              title="Pay this order"
                                          >
                                              Pay Now
                                          </button>
                                      )}
                                                                             {canCancelOrder(o) ? (
                                           <button
                                               onClick={() => handleCancelOrder(o.orderNumber)}
                                               disabled={loading}
                                               className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                           >
                                               {loading && cancelOrderNumber === o.orderNumber ? 'Cancelling...' : 'Cancel'}
                                           </button>
                                       ) : (
                                           <button
                                               disabled
                                               className="px-3 py-1 bg-gray-300 text-gray-500 text-sm rounded-md cursor-not-allowed"
                                               title={
                                                   o.delivery_status === 'cancelled' ? 'Already cancelled' : 
                                                   o.delivery_status === 'delivered' ? 'Already delivered' : 
                                                   'Cannot cancel'
                                               }
                                           >
                                               Cancel
                                           </button>
                                       )}
                                  </div>
                              </td>
                          </tr>
                      );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Cancel Order Confirmation Modal */}
        {showCancelConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Cancel Order
                    </h3>
                    <p className="text-gray-600 mb-6">
                        Are you sure you want to cancel order <strong>#{cancelOrderNumber}</strong>? 
                        This action cannot be undone.
                    </p>
                    <div className="flex space-x-3">
                        <button
                            onClick={cancelCancelOrder}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Keep Order
                        </button>
                        <button
                            onClick={confirmCancelOrder}
                            disabled={loading}
                            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                            {loading ? 'Cancelling...' : 'Cancel Order'}
                        </button>
                    </div>
                </div>
            </div>
        )}
      </div>
    );
}

export default Orders